import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { Plus, Pencil, Trash2, MoveUp, MoveDown } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

export default function CustomFieldsManagement() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);

  // Form state
  const [fieldKey, setFieldKey] = useState("");
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState<"text" | "number" | "date" | "select" | "checkbox">("text");
  const [targetTable, setTargetTable] = useState<"dailyLogs" | "chemicalLogs">("dailyLogs");
  const [isRequired, setIsRequired] = useState(false);
  const [selectOptions, setSelectOptions] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);

  const { data: customFields, refetch } = trpc.customFields.list.useQuery();
  const createMutation = trpc.customFields.create.useMutation();
  const updateMutation = trpc.customFields.update.useMutation();
  const deleteMutation = trpc.customFields.delete.useMutation();



  const resetForm = () => {
    setFieldKey("");
    setLabel("");
    setFieldType("text");
    setTargetTable("dailyLogs");
    setIsRequired(false);
    setSelectOptions("");
    setDefaultValue("");
    setDisplayOrder(0);
    setEditingField(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (field: any) => {
    setEditingField(field);
    setFieldKey(field.fieldKey);
    setLabel(field.label);
    setFieldType(field.fieldType);
    setTargetTable(field.targetTable);
    setIsRequired(field.isRequired);
    setSelectOptions(field.options?.values?.join(", ") || "");
    setDefaultValue(field.defaultValue || "");
    setDisplayOrder(field.displayOrder);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!label.trim()) {
      toast.error("Label is required");
      return;
    }

    if (!editingField && !fieldKey.trim()) {
      toast.error("Field key is required");
      return;
    }

    const options = fieldType === "select" && selectOptions.trim()
      ? { values: selectOptions.split(",").map(s => s.trim()).filter(Boolean) }
      : undefined;

    try {
      if (editingField) {
        await updateMutation.mutateAsync({
          id: editingField.id,
          label,
          fieldType,
          isRequired,
          options,
          defaultValue: defaultValue || undefined,
          displayOrder,
        });
        toast.success("Custom field updated");
      } else {
        await createMutation.mutateAsync({
          fieldKey,
          label,
          fieldType,
          targetTable,
          isRequired,
          options,
          defaultValue: defaultValue || undefined,
          displayOrder,
        });
        toast.success("Custom field created");
      }
      setDialogOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to save custom field");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this custom field?")) return;

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Custom field deleted");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete custom field");
    }
  };

  const handleToggleActive = async (field: any) => {
    try {
      await updateMutation.mutateAsync({
        id: field.id,
        isActive: !field.isActive,
      });
      toast.success(field.isActive ? "Custom field deactivated" : "Custom field activated");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to update custom field");
    }
  };

  const dailyLogsFields = customFields?.filter(f => f.targetTable === "dailyLogs") || [];
  const chemicalLogsFields = customFields?.filter(f => f.targetTable === "chemicalLogs") || [];

  return (
    <div className="container py-8 max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/")}>
            ← Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Custom Fields</h1>
            <p className="text-muted-foreground">Define optional fields for daily logs</p>
          </div>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Custom Field
        </Button>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Daily Log Fields</CardTitle>
            <CardDescription>Custom fields that appear on the daily log entry form</CardDescription>
          </CardHeader>
          <CardContent>
            {dailyLogsFields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No custom fields defined</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Field Key</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyLogsFields.map((field) => (
                    <TableRow key={field.id}>
                      <TableCell className="font-medium">{field.label}</TableCell>
                      <TableCell className="font-mono text-sm">{field.fieldKey}</TableCell>
                      <TableCell className="capitalize">{field.fieldType}</TableCell>
                      <TableCell>{field.isRequired ? "Yes" : "No"}</TableCell>
                      <TableCell>{field.displayOrder}</TableCell>
                      <TableCell>
                        <Switch
                          checked={field.isActive}
                          onCheckedChange={() => handleToggleActive(field)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(field)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(field.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Chemical Log Fields</CardTitle>
            <CardDescription>Custom fields that appear on the chemical tracking section</CardDescription>
          </CardHeader>
          <CardContent>
            {chemicalLogsFields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No custom fields defined</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Field Key</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chemicalLogsFields.map((field) => (
                    <TableRow key={field.id}>
                      <TableCell className="font-medium">{field.label}</TableCell>
                      <TableCell className="font-mono text-sm">{field.fieldKey}</TableCell>
                      <TableCell className="capitalize">{field.fieldType}</TableCell>
                      <TableCell>{field.isRequired ? "Yes" : "No"}</TableCell>
                      <TableCell>{field.displayOrder}</TableCell>
                      <TableCell>
                        <Switch
                          checked={field.isActive}
                          onCheckedChange={() => handleToggleActive(field)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(field)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(field.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingField ? "Edit Custom Field" : "Add Custom Field"}</DialogTitle>
            <DialogDescription>
              Define a custom field that will appear on the daily log form
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {!editingField && (
              <div>
                <Label htmlFor="fieldKey">Field Key *</Label>
                <Input
                  id="fieldKey"
                  value={fieldKey}
                  onChange={(e) => setFieldKey(e.target.value)}
                  placeholder="e.g., equipment_hours"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unique identifier (lowercase, underscores only)
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="label">Label *</Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Equipment Hours"
              />
            </div>

            {!editingField && (
              <div>
                <Label htmlFor="targetTable">Target Section *</Label>
                <Select value={targetTable} onValueChange={(v: any) => setTargetTable(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dailyLogs">Daily Log</SelectItem>
                    <SelectItem value="chemicalLogs">Chemical Tracking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="fieldType">Field Type *</Label>
              <Select value={fieldType} onValueChange={(v: any) => setFieldType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="select">Select (Dropdown)</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {fieldType === "select" && (
              <div>
                <Label htmlFor="selectOptions">Options (comma-separated)</Label>
                <Textarea
                  id="selectOptions"
                  value={selectOptions}
                  onChange={(e) => setSelectOptions(e.target.value)}
                  placeholder="Option 1, Option 2, Option 3"
                  rows={3}
                />
              </div>
            )}

            <div>
              <Label htmlFor="defaultValue">Default Value</Label>
              <Input
                id="defaultValue"
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                placeholder="Optional"
              />
            </div>

            <div>
              <Label htmlFor="displayOrder">Display Order</Label>
              <Input
                id="displayOrder"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch id="isRequired" checked={isRequired} onCheckedChange={setIsRequired} />
              <Label htmlFor="isRequired">Required field</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingField ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
